/**
 * DeleteVersionDialog — confirm-before-delete dla wersji komponentu.
 *
 * Bckend rzuca 409 z `reason` jeśli któryś guard nie przejdzie:
 *   ALREADY_ACTIVE, HAS_REFERENCES, WOULD_LEAVE_GAP, MANAGED_BY_SEED,
 *   VERSION_MISMATCH.
 *
 * @see AC-10 in 2026-05-05-billing-pricing-admin-ui spec
 */
import React, { useEffect, useRef, useState } from "react";
import { deletePricingComponentVersion } from "../../../../services/adminProductCatalogApi";
import type {
  PricingComponentVersion,
  PricingMutationConflictReason,
} from "../../../../types/adminBilling";

interface DeleteVersionDialogProps {
  version: PricingComponentVersion;
  brandId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteVersionDialog: React.FC<DeleteVersionDialogProps> = ({
  version,
  brandId,
  onClose,
  onSuccess,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{
    reason: PricingMutationConflictReason;
    extra?: Record<string, unknown>;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Auto-focus first interactive element on open (a11y)
  const firstFocusRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    firstFocusRef.current?.focus();
  }, []);

  // Escape-to-close (a11y)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSubmit = async () => {
    setError(null);
    setConflict(null);
    if (version.version === undefined) {
      setError("Brak optimistic-lock value (odśwież widok)");
      return;
    }
    setIsSubmitting(true);
    try {
      await deletePricingComponentVersion(version.id, brandId, version.version);
      // Brief success feedback before modal closes (UX MEDIUM batch E task 3).
      setJustSaved(true);
      setTimeout(() => onSuccess(), 800);
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status: number;
          data?: {
            reason?: PricingMutationConflictReason;
            message?: string;
            orderCount?: number;
            currentVersion?: number;
          };
        };
      };
      if (err.response?.status === 409 && err.response.data?.reason) {
        setConflict({
          reason: err.response.data.reason,
          extra: err.response.data,
        });
      } else {
        setError(err.response?.data?.message ?? "Nieoczekiwany błąd");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-version-dialog-title"
        className="bg-white rounded-lg max-w-md w-full mx-4"
      >
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3
            id="delete-version-dialog-title"
            className="text-lg font-semibold text-red-700"
          >
            Usuń wersję
          </h3>
        </div>
        <div className="p-6 space-y-3">
          {conflict ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              <p className="font-medium">{conflictMessage(conflict.reason)}</p>
              {conflict.reason === "HAS_REFERENCES" &&
                typeof conflict.extra?.orderCount === "number" && (
                  <p className="text-xs mt-1">
                    Liczba zamówień: {conflict.extra.orderCount}
                  </p>
                )}
            </div>
          ) : (
            <>
              <p className="text-sm text-neutral-700">
                Wersja zostanie <strong>trwale usunięta</strong>. Operacja
                wymaga, by wersja nie była jeszcze aktywna i nie miała
                referencji w zamówieniach.
              </p>
              <div className="bg-neutral-50 p-3 rounded text-xs">
                <div>
                  <span className="font-medium">Obowiązuje od:</span>{" "}
                  {version.validFrom
                    ? new Date(version.validFrom).toLocaleString("pl-PL")
                    : "—"}
                </div>
                <div>
                  <span className="font-medium">Obowiązuje do:</span>{" "}
                  {version.validTo
                    ? new Date(version.validTo).toLocaleString("pl-PL")
                    : "∞"}
                </div>
                {version.calculator && (
                  <div>
                    <span className="font-medium">Kalkulator:</span>{" "}
                    {version.calculator.name}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  id="delete-version-confirm"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <label htmlFor="delete-version-confirm">
                  Rozumiem, że operacja jest nieodwracalna
                </label>
              </div>
            </>
          )}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button
            ref={firstFocusRef}
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded"
          >
            {conflict ? "Zamknij" : "Anuluj"}
          </button>
          {!conflict && (
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={isSubmitting || !confirmed || justSaved}
              className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50"
            >
              {justSaved
                ? "Usunięto!"
                : isSubmitting
                ? "Usuwanie..."
                : "Usuń wersję"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function conflictMessage(reason: PricingMutationConflictReason): string {
  switch (reason) {
    case "ALREADY_ACTIVE":
      return "Wersja jest już aktywna — nie można jej usunąć";
    case "HAS_REFERENCES":
      return "Wersja ma referencje w zamówieniach — nie można jej usunąć";
    case "WOULD_LEAVE_GAP":
      return "Usunięcie zostawiłoby lukę w pokryciu cenowym";
    case "MANAGED_BY_SEED":
      return "Wersja jest zarządzana przez seed — DELETE zablokowane (dozwolony tylko PATCH validTo)";
    case "VERSION_MISMATCH":
      return "Ktoś inny zmienił wersję — odśwież widok";
    default:
      return "Konflikt — operacja nieudana";
  }
}
